import { api } from 'mixins/api'

export default {
  namespaced: true,

  state: {
    data: {},
    logs: [],
    documents: [],
    documentsLoading: false,
    acts: [],
    actsChain: [],
    actsLoading: false,
    actsChainLoading: false,
    actsError: false,
    balance: 0,
    users: [],
    managers: [],
    requisites: {},
    region: '',
    paymentMethods: [],
    bonuses: 0,
  },

  mutations: {
    setProject (state, project) {
      state.data = Object.assign({}, state.data, project)
      state.users = project.users
      state.data.owner = state.users
        ? state.users.find(user => user.is_owner)
        : state.data.owner
      state.requisites = project.payment_data
      state.paymentMethods = project.payment_methods
    },

    setProjectManager (state, manager) {
      state.data.manager = manager
    },

    setProjectStatus (state, status) {
      state.data.status = status
    },

    setProjectDomains (state, domains) {
      state.data.domains = domains
    },

    setProjectOwner (state, owner) {
      state.data.owner = owner
    },

    setProjectSsl (state, value) {
      state.data.ssl = value
    },

    setProjectBlocked (state, value) {
      state.data.is_blocked = value
    },

    setProjectOwnerId (state, owner_id) {
      state.data.owner_id = owner_id
    },

    setLogs (state, logs) {
      state.logs = logs
    },

    setUsers (state, users) {
      state.data.users = users
      state.managers = state.data.users.filter(user => !user.is_owner)
      state.data.owner = state.data.users ? state.data.users.find(
        user => user.is_owner) : state.data.owner
    },

    setUser (state, user) {
      state.users.push(user)
    },

    setManagers (state, managers) {
      managers = managers.filter(user => !user.is_owner)
      state.managers = managers
    },

    setManager (state, manager) {
      state.managers.push(manager)
    },

    setRequisites (state, requisites) {
      state.requisites = requisites
    },

    deleteLog (state, logId) {
      state.logs = state.logs.filter((log) => log.id !== logId)
    },

    setDocuments (state, documents) {
      state.documents = documents
    },

    setDocumentsLoading (state, documentsLoading) {
      state.documentsLoading = documentsLoading
    },

    deleteDocument (state, documentId) {
      state.documents = state.documents.filter(
        (document) => document.id !== documentId)
    },

    setActs (state, acts) {
      acts = acts.map(act => {
        act.sending = false
        act.regenerating = false
        act.batchSending = false
        return act
      })

      state.acts = acts
    },

    setActsChain (state, actsChain) {
      actsChain = actsChain.map(act => {
        act.sending = false
        act.regenerating = false
        act.batchSending = false
        return act
      })

      state.actsChain = actsChain
    },

    setAct (state, act) {
      const i = state.acts.findIndex((oldAct) => oldAct.id === act.id)
      const newActs = [...state.acts]
      // newActs[i] = act;
      newActs[i] = { ...act, id: act.id }
      state.acts = newActs
    },

    setActsError (state, status) {
      state.actsError = status
    },

    setChainAct (state, act) {
      const i = state.actsChain.findIndex((oldAct) => oldAct.id === act.id)
      const newActs = [...state.actsChain]
      // newActs[i] = act;
      newActs[i] = { ...act, id: act.id }
      state.actsChain = newActs
    },

    setActsLoading (state, actsLoading) {
      state.actsLoading = actsLoading
    },

    setActsChainLoading (state, actsChainLoading) {
      state.actsChainLoading = actsChainLoading
    },

    deleteAct (state, actId) {
      state.acts = state.acts.filter((act) => act.id !== actId)
      state.actsChain = state.actsChain.filter((act) => act.id !== actId)
    },

    setBalance (state, balance) {
      state.balance = balance
    },

    setPaymentMethods (state, methods) {
      state.paymentMethods = methods
    },

    setBonuses (state, bonuses) {
      state.bonuses = bonuses
    },
  },

  actions: {
    async fetchProject ({ commit }, projectId) {
      const { data } = await api.get(`projects/${projectId}`)

      if (!data.payment_data) {
        data['payment_data'] = {
          project_id: data.id,
          company_name: '',
          company_type: 0,
          edrpou: '',
          inn: '',
          legal_address: '',
          actual_address: '',
          user_position: '',
          full_name: '',
          iban: '',
          vchasno_email: '',
          is_active_vchasno: false,
        }
      }
      commit('setProject', data)
      commit('setUsers', data.users)
    },

    async fetchLogs ({ commit }, projectId) {
      const { data } = await api.get(`projects/${projectId}/ci_logs`)

      commit('setLogs', data)
    },

    async deleteLog ({ commit }, { projectId, logId }) {
      const { data } = await api.delete(
        `projects/${projectId}/ci_logs/${logId}`)

      if (data) {
        commit('deleteLog', logId)
      }

      return data
    },

    async fetchDocuments ({ commit }, projectId) {
      commit('setDocumentsLoading', true)
      try {
        const { data } = await api.get(`projects/${projectId}/documents/`, {
          params: {
            types: ['TRANSFER_LICENSE_WITH_CONTRACT', 'PERSONAL-CONTRACT'],
          },
        })

        if (data.errorMessage) {
          console.error(data.errorMessage)
          commit('setDocumentsLoading', false)
          return
        }

        commit('setDocuments', data)
      } catch (e) {
        return Promise.reject(
          new Error('При загрузке документов возникла ошибка'))
      }
      commit('setDocumentsLoading', false)
    },

    async generateActStartPay ({ commit }, projectId) {
      try {
        const { data } = await api.post(`projects/${projectId}/documents/`, {
          types: ['START-PAY'],
        })

        return data
      } catch (e) {
        return Promise.reject(
          new Error('При создании документа возникла ошибка'))
      }
    },

    async generateLicenseWithContract ({ commit }, { projectId, createdAt }) {
      try {
        const { data } = await api.post(`projects/${projectId}/documents/`, {
          types: ['PERSONAL-CONTRACT'],
          createdAt,
        })

        return data
      } catch (e) {
        return Promise.reject(
          new Error('При создании документа возникла ошибка'))
      }
    },

    async sendDocument ({ commit }, { document }) {
      try {
        const { data } = await api.post(`sender`, {
          documentId: document.id,
        })

        return data
      } catch (e) {
        return Promise.reject(
          new Error('При отправке документа возникла ошибка'))
      }
    },

    async sendAct ({ commit }, { projectId, documentId }) {
      try {
        const { data } = await api.get(
          `/projects/${projectId}/acts/send-act/${documentId}`)

        return data
      } catch (e) {
        return Promise.reject(
          new Error('При отправке документа возникла ошибка'))
      }
    },

    async deleteDocument ({ commit }, { projectId, documentId }) {
      try {
        const { data } = await api.delete(
          `projects/${projectId}/documents/${documentId}`)
        commit('deleteDocument', documentId)

        return data
      } catch (e) {
        return Promise.reject(
          new Error('При удалении документа возникла ошибка'))
      }
    },

    async fetchActs ({ commit }, projectId) {
      commit('setActsLoading', true)
      commit('setActsError', false)

      try {
        const { data } = await api.get(`projects/${projectId}/acts/`, {
          params: {
            types: ['ACT-BILL', 'START-PAY'],
          },
        })

        commit('setActs', data)
      } catch (e) {
        commit('setActsLoading', false)
        commit('setActsError', true)

        return Promise.reject(new Error('При загрузке актов возникла ошибка'))
      }
      commit('setActsLoading', false)
    },

    async fetchActsChain ({ commit }, { projectId, documentId, billNumber }) {
      commit('setActsChainLoading', true)
      try {
        const { data } = await api.get(
          (`/projects/${projectId}/acts/documents-list/${documentId}/${billNumber}`))

        commit('setActsChain', data)
      } catch (e) {
        return Promise.reject(new Error('При загрузке актов возникла ошибка'))
      }
      commit('setActsChainLoading', false)
    },

    async deleteAct ({ commit }, { projectId, actId }) {
      try {
        const { data } = await api.delete(
          `projects/${projectId}/acts/${actId}`)

        if (data.hasOwnProperty('Error')) {
          return {
            status: false, error: data.Error,
          }
        }

        commit('deleteAct', actId)

        return {
          status: true,
        }
      } catch (e) {
        return Promise.reject(new Error('При удалении акта возникла ошибка'))
      }
    },

    async regenerateAct ({ commit }, { projectId, actId }) {
      try {
        const response = await api.post(
          `projects/${projectId}/acts/${actId}`)
        commit('setAct', response.data.document)
        commit('setChainAct', response.data.document)

        return response.data
      } catch (e) {
        return Promise.reject(
          new Error('При перегенерации акта возникла ошибка'))
      }
    },

    async batchVchasnoUpload ({ commit, dispatch }, { projectId, document }) {
      try {
        const response = await api.post(
          `projects/${projectId}/acts/batchVchasnoUpload`, {
            billId: document.bill_id,
          })

        commit('setAct', response.data.document)

        return response.data
      } catch (e) {
        return Promise.reject(new Error('При выгрузке актов возникла ошибка'))
      }
    },

    async fetchBalance ({ commit }, projectId) {
      const response = await api.get(`projects/${projectId}/balance`)

      commit('setBalance', response.data)
    },

    async fetchBonuses ({ commit }, projectId) {
      const response = await api.get(`projects/${projectId}/bonuses`)

      commit('setBonuses', response.data)
    },

    async deleteLimbCache ({ commit }, project) {
      try {
        const { data } = await api.get(
          `projects/${project.id}/delete-limb-cache`, project)

        return project
      } catch (e) {
        const { data } = e.response

        return Promise.reject(new Error(data.errorMessage))
      }
    },

    async updateCompanyInfo ({ commit }, project) {
      try {
        await api.get(`projects/${project.id}/update-intercom-info`, project)

        return project
      } catch (e) {
        return Promise.reject(new Error('Unable to queue update'))
      }
    },

    async syncMcpStatus ({ commit }, project) {
      try {
        const { data } = await api.get(`projects/${project.id}/sync`)

        return data
      } catch (e) {
        const message = e.response?.data?.message || 'Unable to sync project status'
        return Promise.reject(new Error(message))
      }
    },
  },

  getters: {
    data: state => {
      return state.data
    },

    users: state => {
      return state.users
    },

    managers: state => {
      return state.managers
    },

    fetched: state => {
      return Object.keys(state.data).length !== 0
    },

    ownerId: state => {
      return state.data.owner_id
    },

    actsTotal: state => {
      return state.acts.length
    },

    balance: state => {
      return state.balance
    },

    bonuses: state => {
      return state.bonuses
    },

    requisites: state => {
      return state.requisites
    },

    paymentMethods: state => {
      return state.paymentMethods
    },

    region: state => {
      return state.data.region
    },
  },
}



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/project.js